// Privacy Policy — last updated 2026-10-02 (PRIVACY_VERSION in server/lib/legal.js)
//
// Tailored to HoopHero's actual data practices: explicit COPPA section
// (player profiles can be under 13), explicit medical-notes treatment,
// named third-party processors (Stripe, Supabase, Resend, Sentry,
// Railway), and clear user-rights coverage.
//
// As with the Terms, this is a strong starting point but not legal
// advice. Have it reviewed by a privacy lawyer before significant
// scale or in response to any regulator inquiry.

import React from 'react';
import LegalLayout, { Section, P, UL, LI, Strong } from './LegalLayout';
import PageMeta from '../components/PageMeta';
import useAppConfig from '../hooks/useAppConfig';

const Privacy = () => {
  const { legal } = useAppConfig();
  return (
  <LegalLayout title="Privacy Policy" lastUpdated="October 2, 2026" version={legal?.privacyVersion}>
    <PageMeta
      title="Privacy Policy | HoopHero"
      description="How HoopHero collects, uses, and protects your information — including specific COPPA protections for youth player data."
      path="/privacy"
    />

    <Section heading="Overview">
      <P>
        This Privacy Policy explains how <Strong>Clarity Growth Pro LLC</Strong> (&quot;HoopHero,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses, shares, and protects
        information when you use our youth basketball program management
        platform (the &quot;Service&quot;), available at <a href="https://www.hoophero.net" style={{ color: '#C2460B' }}>
          hoophero.net
        </a>.
      </P>
      <P>
        We&rsquo;ve tried to write this in plain English. If anything is
        unclear, email <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a> and we&rsquo;ll explain.
      </P>
    </Section>

    <Section heading="1. Information We Collect">
      <P>
        We collect the following categories of information, depending on whether
        you are a Coach, a Parent, or a player whose data was submitted by a
        parent or coach:
      </P>
      <P><Strong>Account information you provide:</Strong></P>
      <UL>
        <LI><Strong>Coaches:</Strong> name, title, email address, phone number, password (hashed — we never see the plaintext), and the program/team details you set up (program name, season, location, age group, monthly fee).</LI>
        <LI><Strong>Parents:</Strong> name, email address, phone number, password (hashed).</LI>
        <LI><Strong>Players:</Strong> name, age, jersey number, on-court positions, optional medical notes, an emergency contact, active/injured status. This information is submitted by a parent or coach; we do not create player accounts and players do not log in.</LI>
      </UL>
      <P><Strong>Payment information:</Strong></P>
      <UL>
        <LI>When a Coach subscribes, our payment processor <Strong>Stripe</Strong> collects the Coach&rsquo;s credit or debit card details directly through Stripe&rsquo;s secure checkout. <Strong>HoopHero never sees, stores, or has access to the full card number.</Strong> Stripe sends us a tokenized customer identifier, subscription status, and a record of which invoices were paid or failed.</LI>
        <LI>For Parent-to-Coach payments (the monthly player fees), HoopHero acts only as a record-keeper. The actual money flows directly through whichever third-party method the Coach has enabled (Venmo, Cash App, PayPal, Zelle, cash). We store the Coach&rsquo;s public handle/link for each enabled method and the Coach&rsquo;s notation of which players have paid each month. We do not collect Parent payment credentials for these methods.</LI>
      </UL>
      <P><Strong>Communications data:</Strong></P>
      <UL>
        <LI>Messages a Coach sends to Parents through the Service, including the subject, body, recipients, and timestamps.</LI>
        <LI>Email delivery metadata (whether a message was delivered, bounced, or opened, where available from our email provider).</LI>
      </UL>
      <P><Strong>Team content:</Strong></P>
      <UL>
        <LI>Team chat messages, RSVPs, attendance, game scores, and snack, volunteer and carpool sign-ups.</LI>
        <LI>Photos and videos uploaded to a team album by the Coach, or by Parents when the Coach allows it. Albums are visible only to the Coach and the Parents in that program.</LI>
        <LI>Feedback you send us from the app&rsquo;s &quot;Send feedback&quot; dialog, with the page you were on.</LI>
      </UL>
      <P><Strong>Technical and usage data:</Strong></P>
      <UL>
        <LI>Server logs: IP address, browser type and version, pages visited, timestamps, and error reports when something in the Service breaks.</LI>
        <LI>Session tokens (JSON Web Tokens) stored in your browser&rsquo;s local storage to keep you logged in between visits.</LI>
        <LI>If you turn on notifications, a push subscription for your browser or phone so we can deliver them.</LI>
        <LI>Anonymous performance/error data collected by our error-monitoring service (Sentry), which may include a user identifier we assign internally.</LI>
      </UL>
    </Section>

    <Section heading="2. How We Use Information">
      <P>We use the information we collect to:</P>
      <UL>
        <LI>Provide, maintain, and improve the Service;</LI>
        <LI>Create and manage your account and (for Coaches) your subscription;</LI>
        <LI>Process subscription payments via Stripe;</LI>
        <LI>Send transactional emails (account confirmations, payment receipts, password resets, coach-to-parent messages routed through the platform);</LI>
        <LI>Send service-related notices (security alerts, changes to these terms, scheduled maintenance);</LI>
        <LI>Detect, prevent, and respond to fraud, abuse, or violations of our Terms;</LI>
        <LI>Comply with legal obligations and respond to lawful requests from public authorities;</LI>
        <LI>Aggregate or anonymize data for our own internal analysis of how the Service is being used (this anonymous data cannot identify any individual).</LI>
      </UL>
      <P>
        <Strong>We do not sell your personal information.</Strong> We do not use
        your information for advertising, behavioral profiling, or ad targeting,
        and we do not share it with advertisers, data brokers, or third-party
        marketers.
      </P>
    </Section>

    <Section heading="3. How We Share Information">
      <P>
        We share information only in the limited circumstances described below:
      </P>
      <P><Strong>With other users you&rsquo;ve chosen to share with:</Strong> A Coach sees the full roster of their own program: player names, jersey numbers, positions, active/injured status, and each player&rsquo;s medical notes and emergency contact. A Parent sees their own family&rsquo;s data, including their own children&rsquo;s medical notes and emergency contacts, and the team-wide information the Coach shares (schedules, announcements, team chat, team albums and sign-up sheets). <Strong>Medical notes and emergency contacts are never shown to other Parents.</Strong> Other families in the program may see your name in team chat and on sign-up sheets.</P>
      <P><Strong>Public scoreboard link:</Strong> If a Coach shares a game&rsquo;s live scoreboard link, anyone with the link can see the team names, the score and the top scorers, shown by first name and last initial only.</P>
      <P><Strong>With our service providers:</Strong> We share information with the third-party platforms below strictly to operate the Service. Each provider acts as a data processor on our behalf and is contractually limited to using the data for that purpose:</P>
      <UL>
        <LI><Strong>Stripe</Strong> — payment processing for Coach subscriptions (<a href="https://stripe.com/privacy" style={{ color: '#C2460B' }}>Stripe Privacy Policy</a>).</LI>
        <LI><Strong>Supabase</Strong> — managed database hosting where we store your account and program data (<a href="https://supabase.com/privacy" style={{ color: '#C2460B' }}>Supabase Privacy</a>).</LI>
        <LI><Strong>Railway</Strong> — application hosting infrastructure (<a href="https://railway.com/legal/privacy" style={{ color: '#C2460B' }}>Railway Privacy</a>).</LI>
        <LI><Strong>Resend</Strong> — transactional email delivery (<a href="https://resend.com/legal/privacy-policy" style={{ color: '#C2460B' }}>Resend Privacy</a>).</LI>
        <LI><Strong>Twilio</Strong> — text message delivery, only for Parents who opt in to texts (<a href="https://www.twilio.com/en-us/legal/privacy" style={{ color: '#C2460B' }}>Twilio Privacy</a>).</LI>
        <LI><Strong>Sentry</Strong> — error and performance monitoring (<a href="https://sentry.io/privacy/" style={{ color: '#C2460B' }}>Sentry Privacy</a>).</LI>
        <LI><Strong>GoDaddy</Strong> — domain registration and DNS (no user data shared beyond what is necessary to operate the domain).</LI>
      </UL>
      <P><Strong>For legal reasons:</Strong> We may disclose information if required by law, valid legal process, or to protect the rights, property, or safety of HoopHero, our users, or the public.</P>
      <P><Strong>In a business transfer:</Strong> If we are acquired by, merge with, or sell substantially all our assets to another company, your information may be transferred. We will notify you of any such change and let you know your choices.</P>
    </Section>

    <Section heading="4. Children's Privacy (COPPA)">
      <P>
        HoopHero is designed specifically for youth basketball programs, and we
        recognize that some players are children under the age of 13. We comply
        with the U.S. Children&rsquo;s Online Privacy Protection Act (COPPA) as
        follows:
      </P>
      <P>
        <Strong>We do not create accounts for children under 13.</Strong> Players
        do not log in to the Service. We have no way for a child to register
        themselves.
      </P>
      <P>
        <Strong>Player profiles are submitted only by a parent, legal guardian,
        or Coach acting on the parent&rsquo;s behalf.</Strong> When a Parent
        registers and enrolls a child in a Coach&rsquo;s program, that Parent is
        providing verifiable consent — through their own account creation and
        the act of submitting the child&rsquo;s data — to HoopHero&rsquo;s
        collection and use of that information for the limited purpose of
        running the basketball program.
      </P>
      <P>
        <Strong>What we collect about minor players:</Strong> name, age, jersey
        number, on-court positions, active/injured status, and any optional
        medical notes the Parent or Coach chooses to enter (e.g., &quot;asthma,
        carries an inhaler&quot;). We collect this information solely to enable the
        Coach and Parent to manage the player&rsquo;s participation in the
        program.
      </P>
      <P>
        <Strong>We do not:</Strong>
      </P>
      <UL>
        <LI>Use children&rsquo;s data for marketing or advertising;</LI>
        <LI>Sell or rent children&rsquo;s data to anyone;</LI>
        <LI>Allow third parties to collect personal information from children through the Service;</LI>
        <LI>Use children&rsquo;s data for purposes beyond running the basketball program;</LI>
        <LI>Knowingly retain children&rsquo;s data longer than necessary.</LI>
      </UL>
      <P>
        <Strong>Parent rights regarding their child&rsquo;s data:</Strong> A
        Parent may at any time review the information we hold about their child,
        request corrections, request deletion, or revoke consent for further
        use. To exercise any of these rights, email <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a> from the email address on file for the Parent&rsquo;s account. We will
        respond within a reasonable time and at no cost.
      </P>
    </Section>

    <Section heading="5. Medical Notes and Other Sensitive Information">
      <P>
        The optional <Strong>medical notes</Strong> field on a player&rsquo;s
        profile is intended for non-emergency information that a coach should be
        aware of during practices and games (allergies, asthma, glasses, etc.).
        We treat any information submitted to this field as sensitive personal
        data and restrict access to the Coach who owns the program and that
        child&rsquo;s own Parent. The same applies to each player&rsquo;s
        emergency contact.
      </P>
      <P>
        <Strong>HoopHero is not a healthcare provider, and the medical notes
        field is not a substitute for medical advice or treatment.</Strong> Do not submit detailed medical records, diagnoses, or anything you would
        consider truly confidential health information. Information you submit
        through HoopHero is not covered by HIPAA, because HoopHero is not a
        covered entity. For medical conditions that require professional
        management, consult a physician.
      </P>
    </Section>

    <Section heading="6. How We Protect Information">
      <UL>
        <LI>All connections to the Service are encrypted in transit using TLS (HTTPS).</LI>
        <LI>Passwords are hashed using bcrypt before being stored — we cannot see your plaintext password and cannot recover it (only reset it).</LI>
        <LI>Payment data is handled by Stripe; HoopHero never receives or stores full card numbers.</LI>
        <LI>We rate-limit login and registration endpoints to deter credential-stuffing and brute-force attacks.</LI>
        <LI>Our infrastructure providers (Supabase, Railway) maintain industry-standard physical and network security controls.</LI>
        <LI>Application errors are monitored in real time so we can respond quickly to issues.</LI>
      </UL>
      <P>
        Despite reasonable safeguards, <Strong>no online service can guarantee
        absolute security.</Strong> If we become aware of a data breach that
        materially affects your information, we will notify you in accordance
        with applicable law.
      </P>
    </Section>

    <Section heading="7. Data Retention">
      <P>
        We retain your information for as long as your account is active and as
        needed to provide the Service. If you delete your account, we will
        delete or anonymize your personal information within 90 days, except
        where we are required by law to retain it longer (for example,
        tax-related records of subscription payments).
      </P>
      <P>
        Server logs and error data are retained for up to 90 days for
        operational and security purposes.
      </P>
    </Section>

    <Section heading="8. Your Rights and Choices">
      <P>Regardless of where you live, you can:</P>
      <UL>
        <LI><Strong>Access</Strong> the personal information we hold about you — most of it is visible directly in your account&rsquo;s Profile and Settings pages.</LI>
        <LI><Strong>Correct</Strong> your information by updating it in your account or emailing us.</LI>
        <LI><Strong>Delete</Strong> your account yourself under <Strong>Your data</Strong> (My Profile for Parents, Settings for Coaches), or by emailing <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
            ruben@claritygrowthpro.com
          </a> from the email on file. A Parent&rsquo;s name, phone number and children&rsquo;s medical notes and emergency contacts are removed at once; a deleted Coach program is permanently deleted after 30 days, and its families are told by email.
        </LI>
        <LI><Strong>Export</Strong> your data yourself under <Strong>Your data</Strong> as JSON or as a zip of spreadsheets, or ask us by email.</LI>
        <LI><Strong>Opt out of marketing email</Strong> at any time using the unsubscribe link in those messages. Transactional emails (receipts, password resets, security alerts, coach-to-parent messages you&rsquo;ve enabled) cannot be opted out of while your account is active.</LI>
      </UL>
      <P>
        <Strong>California residents</Strong> have additional rights under the
        California Consumer Privacy Act (CCPA), including the right to know what
        categories of personal information we&rsquo;ve collected, the right to
        delete that information, and the right not to be discriminated against
        for exercising those rights. Use the email address above for any CCPA
        request and we will verify your identity and respond within 45 days.
      </P>
      <P>
        Residents of other states with comprehensive consumer privacy laws
        (Colorado, Connecticut, Utah, Virginia, etc.) have similar rights and
        can use the same email channel.
      </P>
    </Section>

    <Section heading="8a. Text Messages (SMS)">
      <P>
        If you are a Parent and you check the &quot;Text me team updates&quot; box when you register (or turn on Text messages in your Profile), we send team communications from your Coach, game reminders, and schedule changes to your mobile number by SMS through our messaging provider, Twilio. Message frequency varies with your team&rsquo;s activity. Message and data rates may apply. Reply STOP to any message to stop receiving texts, START to resume, or HELP for help. Your Coach never sees a charge for texting you, and we never sell or share your number for marketing.
      </P>
    </Section>

    <Section heading="9. Cookies and Local Storage">
      <P>
        HoopHero uses your browser&rsquo;s local storage to keep you logged in
        (we store a session token there). This is essential for the Service to
        function. We do not use third-party advertising or tracking cookies. Our
        error-monitoring provider may set a first-party identifier for
        deduplicating error reports.
      </P>
      <P>
        You can clear your browser&rsquo;s local storage at any time, but doing
        so will log you out of the Service.
      </P>
    </Section>

    <Section heading="10. International Users">
      <P>
        HoopHero is operated from the United States and intended for users
        located in the United States. If you access the Service from outside the
        U.S., you understand that your information will be transferred to,
        stored, and processed in the U.S.
      </P>
    </Section>

    <Section heading="11. Changes to This Policy">
      <P>
        We may update this Privacy Policy from time to time. If we make a
        material change — meaning a change that meaningfully affects how we
        collect, use, or share your information — we will notify you by email
        and post a notice in the Service before the change takes effect. The
        &quot;Last updated&quot; date at the top of this document always reflects the
        current version.
      </P>
    </Section>

    <Section heading="12. Contact Us">
      <P>
        For privacy questions, data-rights requests, or to report a potential
        privacy issue:
      </P>
      <P>
        <Strong>Email:</Strong> <a href="mailto:ruben@claritygrowthpro.com" style={{ color: '#C2460B' }}>
          ruben@claritygrowthpro.com
        </a>
        <br />
        <Strong>Phone:</Strong> <a href="tel:+19049212519" style={{ color: '#C2460B' }}>
          (904) 921-2519
        </a>
      </P>
      <P>
        <Strong>Mail:</Strong><br />
        Clarity Growth Pro LLC<br />
        Attn: HoopHero Privacy<br />
        24073 Flora Parke Blvd.<br />
        Fernandina Beach, FL 32034
      </P>
    </Section>

  </LegalLayout>
  );
};

export default Privacy;
